import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FieldsList from "./fields-list.js"; // Import the FieldsList component
import { api } from "../../maps/data.js";

const FieldsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [stores, setStores] = useState([]);
  const [clients, setClients] = useState([]);
  const [edges, setEdges] = useState([]);

  const [selectedStore, setSelectedStore] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedEdge, setSelectedEdge] = useState("");
  const [fieldsData, setFieldsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api({ type: "getClients" });
        setClients(res);
      } catch (err) {
        console.error("Failed to fetch clients:", err);
        setError("Unable to fetch clients. Please try again later.");
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    const clientId = searchParams.get("client");
    const storeId = searchParams.get("store");
    const edgeId = searchParams.get("edge");

    if (clientId) {
      setSelectedClient(clientId);
      const savedStores = localStorage.getItem(`stores_${clientId}`);
      if (savedStores) {
        setStores(JSON.parse(savedStores));
        if (storeId) {
          setSelectedStore(storeId);
        }
      } else {
        fetchStores(clientId);
      }

      if (storeId) {
        const savedEdges = localStorage.getItem(`edges_${storeId}`);
        if (savedEdges) {
          setEdges(JSON.parse(savedEdges));
          if (edgeId) {
            setSelectedEdge(edgeId);
          }
        } else {
          fetchEdges(storeId);
        }
      }
    }
  }, [searchParams]);

  const fetchStores = async (clientId) => {
    try {
      const res = await api({ type: "getStores", data: { clientId } });
      setStores(res);
      localStorage.setItem(`stores_${clientId}`, JSON.stringify(res));
    } catch (err) {
      console.error("Failed to fetch stores:", err);
      setError("Unable to fetch stores. Please try again later.");
    }
  };

  const fetchEdges = async (siteId) => {
    try {
      const res = await api({ type: "getEdges", data: { siteId } });
      setEdges(res);
      localStorage.setItem(`edges_${siteId}`, JSON.stringify(res));
    } catch (err) {
      console.error("Failed to fetch edges:", err);
      setError("Unable to fetch edges. Please try again later.");
    }
  };

  useEffect(() => {
    if (selectedEdge) {
      setLoading(true);
      setError(null);
      navigate(
        `?client=${selectedClient}&store=${selectedStore}&edge=${selectedEdge}`
      );

      const fetchFields = async () => {
        try {
          const res = await api({
            type: "getFields",
            data: { edgeId: selectedEdge },
          });
          setFieldsData(res);
        } catch (err) {
          console.error("Failed to fetch fields data:", err);
          setError("Unable to fetch fields data. Please try again later.");
        } finally {
          setLoading(false);
        }
      };

      fetchFields();
    }
  }, [selectedEdge, selectedClient, selectedStore]);

  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    setSelectedStore("");
    setSelectedEdge("");
    setFieldsData(null);
    setStores([]);
    setEdges([]);
    navigate(`?client=${clientId}`);
    fetchStores(clientId);
  };

  const handleStoreChange = (storeId) => {
    setSelectedStore(storeId);
    setSelectedEdge("");
    setFieldsData(null);
    setEdges([]);
    navigate(`?client=${selectedClient}&store=${storeId}`);
    fetchEdges(storeId);
  };

  const handleEdgeChange = (edgeId) => {
    setSelectedEdge(edgeId);
    setFieldsData(null);
    setError(null);
    setLoading(true);
    navigate(`?client=${selectedClient}&store=${selectedStore}&edge=${edgeId}`);
  };

  const handleClearAll = () => {
    setSelectedClient("");
    setSelectedStore("");
    setSelectedEdge("");
    setStores([]);
    setEdges([]);
    setFieldsData(null);
    setError(null);
    setLoading(false);
    navigate("?"); // Reset URL to the initial state without any parameters
  };

  return (
    <div className="flex flex-col h-screen">
      {/* AppBar */}

      {/* Main Container */}
      <div className="flex-1 overflow-y-auto bg-gray-100 py-8 px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Client Select */}
          <div>
            <label htmlFor="client" className="block text-lg font-medium mb-2">
              Select Client
            </label>
            <select
              id="client"
              value={selectedClient}
              onChange={(e) => handleClientChange(e.target.value)}
              className="w-full p-2 border rounded-lg bg-white"
            >
              <option value="">None</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          {/* Store Select */}
          <div>
            <label htmlFor="store" className="block text-lg font-medium mb-2">
              Select Store
            </label>
            <select
              id="store"
              value={selectedStore}
              onChange={(e) => handleStoreChange(e.target.value)}
              className="w-full p-2 border rounded-lg bg-white"
              disabled={!selectedClient}
            >
              <option value="">None</option>
              {stores.map((store) => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>

          {/* Edge Select */}
          <div>
            <label htmlFor="edge" className="block text-lg font-medium mb-2">
              Select Edge
            </label>
            <select
              id="edge"
              value={selectedEdge}
              onChange={(e) => handleEdgeChange(e.target.value)}
              className="w-full p-2 border rounded-lg bg-white"
              disabled={!selectedStore}
            >
              <option value="">None</option>
              {edges.map((edge) => (
                <option key={edge._id} value={edge._id}>
                  {edge.floorName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear All Button */}
        <div className="mt-4">
          <button
            onClick={handleClearAll}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg"
          >
            Clear All Selections
          </button>
        </div>

        {/* Fields List Component */}
        <div className="mt-6">
          {fieldsData && (
            <FieldsList
              data={fieldsData}
              loading={loading}
              error={error}
               siteId={selectedStore}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldsPage;
